import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgKLoggerService } from '@kin/ngk-logger';
import { WA_LOCAL_STORAGE } from '@ng-web-apis/common';
import { Subscription, firstValueFrom } from 'rxjs';

import { LegacyCreateCirService } from 'app/home/common/services/legacy/create-cir/create-cir.service';
import { LegacyCustomerInputResponseService } from 'app/home/common/services/legacy/customer-input-response/customer-input-response.service';
import { LegacyPackagesService } from 'app/home/common/services/legacy/packages/packages.service';
import { LegacyApiPagesResponse, LegacyPagesApiService } from 'app/home/common/services/legacy/quote-pages';
import { LegacyQuotePagesPath } from 'app/home/common/services/legacy/quote-pages/models/paths.config';
import { TDateISODate } from 'app/home/common/utils/iso-date-type/iso-date.type';
import { mapLegacyPathToFormKey } from 'app/home/create-quote/form-config/map-legacy-path-to-form-key';
import { mapLegacyPathToLegacyPayload } from 'app/home/create-quote/form-config/map-legacy-path-to-legacy-payload';
import { quoteFormLegacyPaths } from 'app/home/create-quote/form-config/quote-form-legacy-paths';
import { mergeSubmittedFields } from 'app/home/create-quote/form-locking/form-lock.util';
import { isCheckWindMitApplicable, mapComparePackageData } from 'app/home/create-quote/forms/compare/compare-utils';
import { LegacyPackageTitle } from 'app/home/create-quote/forms/compare/compare.types';
import { WA_LOCAL_STORAGE_DRAFT_TOKEN_KEY } from 'app/home/create-quote/services/entry/quote-draft-entry.service';
import { GlobalStoreActionsService } from 'app/home/global-store/global-store-actions.service';
import { QuoteDraftCoApplicant, QuoteDraftSelectorState } from 'app/home/global-store/quote-draft/quote-draft.model';
import { QuoteDraftStore } from 'app/home/global-store/quote-draft/quote-draft.store';
import { QuoteFormOptionsStore } from 'app/home/global-store/quote-form-options';
import { UserStore } from 'app/home/global-store/user/user.store';
import { FullstoryService } from 'app/shared/services/fullstory/fullstory.service';

import {
  CONSTRUCTION_TYPES,
  EXTERIOR_WALLS,
  FOUNDATIONS,
  FUNNEL_WALK,
  INSURANCE_STATUSES,
  POLICY_TYPES,
  PROPERTY_TYPES,
  ROOF_SHAPES,
  type SavePropertyDetailsArgs,
  WALK_BY_PATH,
  remainingQuestions,
  validatePropertyDetailsArgs,
} from './webmcp-funnel-walk';
import { WebMcpTool, WebMcpToolExecutedPayload, WebMcpToolOutcome, describeError, errorOutcome, okOutcome, toWireResult } from './webmcp-tools.types';

/**
 * ⚠️ EXPERIMENTAL FEATURE ⚠️
 *
 * `ExperimentalWebMcpService` registers Kin's WebMCP tools with the browser's WebMCP API so
 * AI agents can drive a home insurance quote — address entry through package selection —
 * without scraping the DOM. Call register() once during app init; every tool is unregistered
 * when the service is destroyed (one AbortController for the whole set).
 *
 * ## What "experimental" means here
 *
 * - **The WebMCP spec itself is not final.** The browser API surface (`navigator.modelContext`,
 *   tool registration, execution semantics) is still a draft (webmachinelearning.github.io/webmcp)
 *   and only available behind flags in Chromium. Anything shaped by that draft may change.
 * - **No stability commitment.** Tool names, argument schemas, output shapes, and error
 *   guidance may change or be removed in any release without notice. Do not build external
 *   agents or integrations against these tool shapes yet; treat the surface as internal
 *   prototyping for evaluation and UAT.
 * - **Limited browser support.** In browsers without WebMCP, register() no-ops (logged at
 *   info level) — the feature degrades silently and never affects the human funnel.
 * - **Telemetry is eval-grade.** FullStory tracks only tool names and success/duration
 *   (`webmcp_tool_executed`); args and outputs are never tracked (PII rule).
 *
 * ## Long-term plan: migrate to Angular's first-party WebMCP support
 *
 * This service is a hand-rolled bridge (imperative registration, hand-serialized outcomes)
 * that exists only because quoting-ui predates Angular's WebMCP integration. When this app
 * is upgraded to **Angular 22**, the plan is to replace this service with Angular's
 * built-in WebMCP support — `provideWebMcpTools()` in the application config, with tool
 * executes running in Angular's injection context (direct `inject()` access to stores and
 * services instead of constructor injection on a bespoke service). The migration should
 * preserve the tool surface itself — names, schemas, annotations, state-guard semantics,
 * and the funnel-walk behavior validated by the specs in this directory — because that
 * surface was designed independently of the registration mechanism. Only the plumbing
 * (registration, lifecycle, wire serialization) moves to Angular. Until then, this file is
 * the single source of truth for the tool surface; new tools belong here, not in Angular
 * decorators.
 *
 * ## Design notes (spec: channel canvas "WebMCP Quote-Funnel Tool Spec", 2026-10-14)
 *
 * - Static registration at init. Tools self-guard against being called in the wrong state
 *   and return structured guidance ("call start_quote first") instead of failing silently.
 * - The funnel's order is API-driven: `save_property_details` walks pages by following each
 *   save response's next page, seeded from the locally recorded current page.
 * - Page payloads are built exactly like the UI save effect builds them: patch the store,
 *   then map the store → CIR payload (`mapLegacyPathToLegacyPayload`) — one mapping, no drift.
 * - PII never leaves: args are never tracked to FullStory, outputs never echo PII values.
 *
 * Spec: https://webmachinelearning.github.io/webmcp/
 */
/**
 * The questions a stopped-on page is actually asking, interpreted the way the page's form
 * interprets it: each form renders only the component with its own `attribute_name`, and the
 * question text is the form template's title — not the API's component labels, which differ
 * in places. Components with no matching walk arg (fields this tool cannot answer) fall back
 * to their label so the agent at least knows to hand off to the UI.
 */
function pageQuestions(page: NonNullable<LegacyApiPagesResponse['page']>, consumed: ReadonlySet<string>): string[] {
  const entry = WALK_BY_PATH[page.path];
  const questions: string[] = [];
  for (const component of page.components ?? []) {
    const arg = entry?.args.find((walkArg) => walkArg.attribute === component.attribute_name);
    if (arg) {
      if (!consumed.has(arg.arg) && !questions.includes(arg.prompt)) {
        questions.push(arg.prompt);
      }
    } else if (
      component.component_type !== 'hidden' && // the UI never renders hidden components (state carriers like include_coapplicant_info)
      typeof component.label === 'string' &&
      component.label.length > 0 &&
      component.label !== component.attribute_name &&
      !questions.includes(component.label)
    ) {
      questions.push(component.label);
    }
  }
  return questions;
}

/**
 * Redirect targets leave the quoting app (resume flows, ejections). Their query strings can
 * embed user PII (e.g. `?email=…`), which never reaches the agent — only the origin + path.
 */
function sanitizeRedirect(url: string): string {
  // Absolute URLs (resume flows, ejections on other hosts): keep origin + path, drop the
  // query. Relative paths are internal funnel routes — pass them through untouched.
  if (!/^https?:\/\//i.test(url)) {
    return url.split('?')[0]!;
  }
  try {
    const parsed = new URL(url);
    return parsed.origin + parsed.pathname;
  } catch {
    return url.split('?')[0]!;
  }
}

/**
 * Maps the app's URL to the Pages API path that renders there — funnel pages only. Form URLs
 * use form keys (dash-case), which differ from the API's page paths in places
 * (`/quote/policy-type` renders `/quote/policy_type`). Everything else under /quote (start,
 * resume, the legacy redirect route itself) is an entry route, not a funnel position.
 */
function funnelPagePathFromUrl(url: string): string | undefined {
  const path = url.split('?')[0]!;
  if (path.startsWith('/thanks')) {
    return path.slice(1) || 'thanks'; // ejection pages: '/thanks/moratorium' → 'thanks/moratorium'
  }
  if (path === '/quote/compare' || path === '/quote/review') {
    return path;
  }
  if (path.startsWith('/quote/')) {
    return (quoteFormLegacyPaths as Record<string, string>)[path.slice('/quote/'.length)];
  }
  return undefined;
}

/** The human-readable questions a page is rendering (component labels, in render order). */
@Injectable({ providedIn: 'root' })
export class ExperimentalWebMcpService implements OnDestroy {
  private createCir = inject(LegacyCreateCirService);
  private pagesApi = inject(LegacyPagesApiService);
  private packagesApi = inject(LegacyPackagesService);
  private customerInputResponseApi = inject(LegacyCustomerInputResponseService);
  private quoteStore = inject(QuoteDraftStore);
  private optionsStore = inject(QuoteFormOptionsStore);
  private logger = inject(NgKLoggerService);
  private userStore = inject(UserStore);
  private storeActions = inject(GlobalStoreActionsService);
  private router = inject(Router);
  private fullstory = inject(FullstoryService);
  private localStorage = inject(WA_LOCAL_STORAGE);
  private registration = new AbortController();
  /**
   * One write at a time. Concurrent saves race on the shared store (each payload is built
   * from it after patching), double-save pages, and can drop arguments when one walk's
   * response moves the funnel under the other. Concurrent writers are told to retry.
   */
  private writeInFlight = false;
  /**
   * The last funnel page observed — from a Pages API response (tool saves) or the app's own
   * navigation (the user driving the UI). Kept here rather than in the quote draft store:
   * it is agent-facing observation state, not quote data, and it must not survive sessions
   * (the session-storage-synced store would replay a stale page after a reload).
   */
  private lastPagePath: string | undefined;
  private routerSubscription: Subscription;

  constructor() {
    // Follow the user's own navigation so agent and user can interleave: the UI's save
    // effect navigates on every user save, which is the movement tool calls don't see.
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const pagePath = funnelPagePathFromUrl(event.urlAfterRedirects);
        if (pagePath) {
          this.lastPagePath = pagePath;
        }
      }
    });
  }

  /** No-ops in browsers without WebMCP. */
  register(): void {
    const modelContext = document.modelContext ?? navigator.modelContext;
    if (!modelContext) {
      console.info('[WebMCP] modelContext not available in this browser; tools not registered');
      return;
    }

    for (const tool of this.buildTools()) {
      const trackedTool = this.withTracking(tool);
      Promise.resolve()
        .then(() => modelContext.registerTool(trackedTool, { signal: this.registration.signal }))
        .catch((err: unknown) => console.warn(`[WebMCP] Failed to register tool "${trackedTool.name}"`, err));
    }
  }

  ngOnDestroy(): void {
    this.registration.abort();
    this.routerSubscription.unsubscribe();
  }

  private buildTools(): WebMcpTool[] {
    return [this.startQuote(), this.getQuoteStatus(), this.saveApplicant(), this.savePropertyDetails(), this.saveCoApplicant(), this.getPackages(), this.selectPackage()];
  }

  // ---------------------------------------------------------------------------
  // 1. start_quote — existing tool, adds annotations only
  // ---------------------------------------------------------------------------

  private startQuote(): WebMcpTool {
    return {
      name: 'start_quote',
      description: `Begins a home insurance quote for a property address.
The address is validated and geocoded. Returns whether Kin offers coverage at
that location and the quote's next step. After calling, the user continues the
quote in this app's UI.`,
      inputSchema: {
        type: 'object',
        properties: {
          street: { type: 'string', description: 'Street address without unit number, e.g. "800 N Bel Air Dr"' },
          city: { type: 'string' },
          state: { type: 'string', description: 'Two-letter US state code, e.g. "FL"' },
          zip: { type: 'string', description: 'Five-digit ZIP code' },
          unit: { type: 'string', description: 'Unit or apartment number, if any' },
        },
        required: ['street', 'city', 'state', 'zip'],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false, consequentialHint: false, untrustedContentHint: false },
      execute: async (args, options) => {
        const { street, city, state, zip, unit } = args as { street?: string; city?: string; state?: string; zip?: string; unit?: string };
        const isCancelled = () => options?.signal?.aborted === true;
        // Validate in code, not just in the schema: the geocoder silently "fixes" malformed
        // input (a missing or non-numeric ZIP geocodes anyway), which can send the quote to
        // an address the agent never confirmed.
        const missing = (['street', 'city', 'state', 'zip'] as const).filter((field) => !args[field] || String(args[field]).trim().length === 0);
        if (missing.length > 0) {
          return errorOutcome(`Missing required address field(s): ${missing.join(', ')}. Provide street, city, state, and zip.`);
        }
        if (!/^[A-Za-z]{2}$/.test(state ?? '')) {
          return errorOutcome('Invalid state. Provide the two-letter US state code, e.g. "FL".');
        }
        if (!/^\d{5}$/.test(zip ?? '')) {
          return errorOutcome('Invalid ZIP code. Provide a five-digit ZIP, e.g. "32810".');
        }
        try {
          // A new quote starts from a clean store — otherwise answers, submittedFields, and
          // the compare selection leak from the previous quote into this one (the store is
          // the payload source). This is the app's own newQuote() reset.
          this.storeActions.newQuote();

          // Mirrors the UI entry flow: the unit is passed separately, not baked into the
          // street address, so geocoding never sees duplicated unit text.
          const response = await firstValueFrom(this.createCir.getCIR({ street_address: `${street}, ${city}, ${state} ${zip}`, unit_number: unit ?? '' }));

          // LegacyCreateCirService maps an unexpected success-without-authentication
          // response to the synthetic '404' redirect — a service error, not a coverage decline.
          if (response.redirect_to === '404') {
            return errorOutcome('Failed to start quote: the quote service returned an unexpected response.');
          }

          if (response.redirect_to) {
            return okOutcome({ success: false, ineligibilityReason: 'Kin does not offer coverage for this address.', redirectTo: response.redirect_to });
          }

          if (isCancelled()) {
            return errorOutcome('Quote start was cancelled.');
          }

          // Persist the CIR token the same way the UI entry flow does: the API interceptor
          // prefers the store's token, the resume flow reads the store, and local storage
          // covers requests that resolve through the legacy path.
          const token = response.authentication?.token ?? '';
          // Mark the session as an active quoting session, exactly like the UI entry flow
          // (QuoteDraftEntryService / QuoteDraftSyncService) — the is-quoting route guard
          // checks this to let the user (and this service's own navigation) reach quote pages.
          this.userStore.setEntryType('click-lead');
          this.userStore.startQuoting();
          this.localStorage?.setItem(WA_LOCAL_STORAGE_DRAFT_TOKEN_KEY, token);
          this.quoteStore.patchStore({
            draftToken: token,
            cirId: response.authentication?.decoded?.data.customer_response_id,
            address: { street: street!, ...(unit && { unit }), city: city!, state: state!, zipcode: zip! },
            displayAddress: response.address?.standardized_address,
          });

          if (response.resumable) {
            return okOutcome({ success: true, resumable: true, nextStep: 'resume-my-quote/resumable' });
          }

          // Poll for the quote's actual next page instead of assuming it.
          const nextPage = await firstValueFrom(this.pagesApi.pollForNextPage());
          if (isCancelled()) {
            return errorOutcome('Quote start was cancelled.');
          }
          this.recordPage(nextPage);
          this.advanceUi(nextPage);
          if (nextPage.redirect_to) {
            return okOutcome({ success: true, redirectedTo: sanitizeRedirect(nextPage.redirect_to), note: 'The quote flow moved early — continue in the app UI.' });
          }
          if (!nextPage.page?.path) {
            return errorOutcome('Failed to start quote: the quote service did not return a next step.');
          }
          return okOutcome({
            success: true,
            validatedAddress: response.address?.standardized_address,
            state: response.address?.state,
            nextStep: nextPage.page.path,
          });
        } catch (err) {
          if ((err as { status?: number })?.status === 422) {
            return okOutcome({ success: false, ineligibilityReason: 'Address could not be validated or is not in a supported state.' });
          }
          return errorOutcome(`Failed to start quote: ${describeError(err)}`);
        }
      },
    };
  }

  // ---------------------------------------------------------------------------
  // 2. get_quote_status — read-only state probe; local read, answers instantly
  // ---------------------------------------------------------------------------

  private getQuoteStatus(): WebMcpTool {
    return {
      name: 'get_quote_status',
      description: `Returns the status of the current quote and what questions still need answering`,
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true, consequentialHint: false, untrustedContentHint: false },
      execute: async () => {
        // Read from locally recorded page state — never pollForNextPage(), which hangs
        // until an in-flight calculation finishes and would defeat the purpose.
        if (!this.quoteStore.cirId()) {
          return okOutcome({ state: 'not_started', nextAction: 'Call start_quote with the user\u2019s property address.' });
        }
        return okOutcome({
          state: this.quoteState(),
          currentPage: this.currentFunnelPage(),
          validatedAddress: this.quoteStore.displayAddress(), // Kin-standardized, not raw input
          answeredForms: Object.keys(this.quoteStore.submittedFields()), // form keys only — never values
          remainingQuestions: this.remainingQuestions(),
          nextAction: this.nextAction(),
        });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // 3. save_applicant — about-you page
  // ---------------------------------------------------------------------------

  private saveApplicant(): WebMcpTool {
    return {
      name: 'save_applicant',
      description: `Saves the user's contact details. Each field is
validated and normalized; invalid fields return guidance so the agent can
re-ask. Returns the next step.`,
      inputSchema: {
        type: 'object',
        properties: {
          first_name: { type: 'string', description: 'Legal first name' },
          last_name: { type: 'string', description: 'Legal last name' },
          email: { type: 'string', description: 'Contact email, e.g. "jamie@example.com"' },
          phone_number: { type: 'string', description: 'US phone in any common format, e.g. "(555) 123-4567"' },
          dob: { type: 'string', description: 'Date of birth, YYYY-MM-DD' },
          opt_in_text: { type: 'boolean', description: 'True when the user agrees to text-message updates' },
        },
        required: ['first_name', 'last_name', 'email', 'phone_number', 'dob'],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false, consequentialHint: false, untrustedContentHint: false },
      execute: async (args, options) => {
        const a = args as { first_name?: string; last_name?: string; email?: string; phone_number?: string; dob?: string; opt_in_text?: boolean };
        const isCancelled = () => options?.signal?.aborted === true;
        const notStarted = this.requireQuoteStarted();
        if (notStarted) {
          return notStarted;
        }

        // Validate strictly in code (loosely in schema): each miss returns guidance for re-asking.
        const firstName = a.first_name?.trim();
        const lastName = a.last_name?.trim();
        if (!firstName || !lastName) {
          return errorOutcome('Missing name. Provide first_name and last_name as separate strings.');
        }
        const email = a.email?.trim() ?? '';
        if (!EMAIL_RE.test(email)) {
          return errorOutcome('Invalid email. Provide it in the format user@example.com.');
        }
        const phoneNumber = a.phone_number?.trim() ?? '';
        if (!PHONE_RE.test(phoneNumber)) {
          return errorOutcome('Invalid phone number. Provide a US phone number, e.g. "(555) 123-4567".');
        }
        if (!isRealDate(a.dob)) {
          return errorOutcome('Invalid date of birth. Provide the date in YYYY-MM-DD format.');
        }

        // Idempotence: re-saving about-you for an already-saved applicant makes the API
        // flag the user as existing and redirect the browser to the cross-origin resume
        // flow — an agent retrying after a network error would bounce the user out of the
        // app. When the store already holds these exact values, skip the re-save.
        const existing = this.quoteStore.applicant();
        const alreadySaved = existing?.firstName === firstName && existing?.lastName === lastName && existing?.email === email && existing?.phoneNumber === phoneNumber && existing?.dob === a.dob;
        if (alreadySaved && this.quoteStore.submittedFields()['about-you']) {
          return okOutcome({
            saved: true,
            nextStep: this.currentFunnelPage(),
            note: 'These contact details are already saved; nothing was re-submitted.',
            ...this.pagePointer(this.currentFunnelPage()),
          });
        }

        this.quoteStore.patchStore({
          applicant: { firstName, lastName, email, phoneNumber, dob: a.dob },
        });
        try {
          const response = await firstValueFrom(this.pagesApi.save(this.pagePayload('/quote/about-you'), '/quote/about-you'));
          if (isCancelled()) {
            return errorOutcome('Save was cancelled.');
          }
          this.recordPage(response);
          this.advanceUi(response);
          this.markSubmitted('/quote/about-you', ['applicant']);
          if (response.redirect_to) {
            return okOutcome({ saved: true, redirectedTo: sanitizeRedirect(response.redirect_to), note: 'The quote flow moved early — continue in the app UI.' });
          }
          return okOutcome({ saved: true, nextStep: response.page?.path, ...this.pagePointer(response.page?.path) });
        } catch (err) {
          if ((err as { status?: number })?.status === 422) {
            return errorOutcome('The quote service rejected the contact details. Re-confirm each field with the user.');
          }
          return errorOutcome(`Failed to save applicant: ${describeError(err)}`);
        }
      },
    };
  }

  // ---------------------------------------------------------------------------
  // 4. save_property_details — batched qualifying questions, API-driven order
  // ---------------------------------------------------------------------------

  private savePropertyDetails(): WebMcpTool {
    return {
      name: 'save_property_details',
      description: `Saves the quote's qualifying answers — policy type (the first
question), insurance status, year built, property details, rental status, and
coverage start date. Accepts a partial set; submits each answered question in
funnel order and returns accepted answers, rejected answers with reasons, and
the remaining questions to ask the user.`,
      inputSchema: {
        type: 'object',
        properties: {
          policy_type: { type: 'string', enum: [...POLICY_TYPES], description: 'Kind of home being insured' },
          insurance_status: { type: 'string', enum: [...INSURANCE_STATUSES], description: 'Whether the user currently has home insurance' },
          year_built: { type: 'string', description: 'Year the home was built, YYYY' },
          property_type: { type: 'string', enum: [...PROPERTY_TYPES], description: 'Type of structure on the property' },
          pool: { type: 'boolean', description: 'Whether the home has a swimming pool' },
          sq_ft: { type: 'number', description: 'Approximate square footage of the home' },
          stories: { type: 'number', description: 'Number of stories in the home' },
          roof_year: { type: 'string', description: 'Year the roof was installed, YYYY' },
          roof_shape: { type: 'string', enum: [...ROOF_SHAPES], description: 'Shape of the roof' },
          construction_type: { type: 'string', enum: [...CONSTRUCTION_TYPES], description: 'How the home was built' },
          exterior_wall: { type: 'string', enum: [...EXTERIOR_WALLS], description: 'Outer wall material' },
          foundation: { type: 'string', enum: [...FOUNDATIONS], description: 'Foundation under the home' },
          insurance_expired_over_30_days: { type: 'boolean', description: 'Whether the previous home insurance expired over 30 days ago (asked after insurance_status no_insurance)' },
          purchase_date: { type: 'string', description: 'When the home was purchased, YYYY-MM-DD' },
          residency: { type: 'number', description: 'Months per year the home is occupied, e.g. 12 for a primary residence' },
          rental_status: { type: 'boolean', description: 'True when the home is a rental' },
          rental_status_number: { type: 'number', description: 'Number of rental units, when the home is a rental' },
          policy_start_date: { type: 'string', description: 'Desired coverage start date, YYYY-MM-DD' },
        },
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false, consequentialHint: false, untrustedContentHint: false },
      execute: async (rawArgs, options) => {
        const args = rawArgs as SavePropertyDetailsArgs;
        const isCancelled = () => options?.signal?.aborted === true;
        const notStarted = this.requireQuoteStarted();
        if (notStarted) {
          return notStarted;
        }
        // Reject the whole call if any provided value is invalid — the Pages API accepts
        // unknown values silently, so nothing reaches it until every field passes the same
        // validation the UI's reactive forms enforce.
        const invalid = validatePropertyDetailsArgs(args);
        if (invalid.length > 0) {
          return okOutcome({ accepted: [], rejected: invalid, note: 'Nothing was saved. Fix the rejected fields and call again with the rest unchanged.' });
        }

        const accepted: string[] = [];
        const rejected: { field: string; reason: string }[] = [];
        // Seed the walk from the locally recorded current page; fall back to the first
        // answered question in funnel order when the position is unknown (e.g. fresh draft).
        let current = this.walkSeed(args);
        const consumed = new Set<string>();
        let lastPage: NonNullable<LegacyApiPagesResponse['page']> | undefined;

        while (current) {
          // The API owns the order: each save response supplies the next page; this table
          // only decides which tool arguments answer the page the API rendered. A page with
          // no entry (co-applicant, unit-number, …) or with no unconsumed argument left for
          // it stops the walk — which is also the loop bound, since every iteration consumes
          // at least one argument. Multi-question pages that re-render the same path with
          // the next question keep walking while arguments for that path remain.
          const entry = WALK_BY_PATH[current];
          const pending = (entry?.args ?? []).filter((arg) => args[arg.arg] !== undefined && !consumed.has(arg.arg));
          if (pending.length === 0) {
            break;
          }
          this.quoteStore.patchStore(Object.assign({}, ...pending.map((arg) => arg.toStore(args))));
          try {
            const response = await firstValueFrom(this.pagesApi.save(this.pagePayload(current), current));
            if (isCancelled()) {
              return errorOutcome('Save was cancelled.');
            }
            for (const arg of pending) {
              accepted.push(arg.arg);
              consumed.add(arg.arg);
            }
            this.recordPage(response);
            this.advanceUi(response);
            this.markSubmitted(
              current,
              pending.map((arg) => arg.storeKey),
            );
            lastPage = response.page;
            if (response.redirect_to) {
              // Funnel branched, completed, or ejected — the API owns what's next, so any
              // not-yet-asked table questions are moot; don't report them as remaining.
              return okOutcome({ accepted, rejected, redirectedTo: sanitizeRedirect(response.redirect_to), note: 'The funnel moved on — call get_quote_status or get_packages to see what is next.' });
            }
            current = response.page?.path as LegacyQuotePagesPath | undefined;
          } catch (err) {
            if ((err as { status?: number })?.status === 422) {
              rejected.push({ field: pending.map((arg) => arg.arg).join(', '), reason: 'The quote service rejected this value — re-confirm it with the user.' });
            } else {
              rejected.push({ field: pending.map((arg) => arg.arg).join(', '), reason: describeError(err) });
            }
            break; // stop at first failure, report the rest
          }
        }
        // When the walk stops on a page the args cannot answer, surface the questions the
        // API is actually rendering there (component labels) so the agent knows what to ask
        // or hand off to the UI for — and name the arguments that would continue the walk.
        const pendingQuestions = lastPage && current && lastPage.path === current ? pageQuestions(lastPage, consumed) : [];
        const nextArgs = (current ? WALK_BY_PATH[current]?.args : undefined)?.filter((arg) => !consumed.has(arg.arg)).map((arg) => arg.arg) ?? [];
        return okOutcome({
          accepted,
          rejected,
          nextStep: current,
          ...(pendingQuestions.length > 0 ? { pendingQuestions } : {}),
          ...(nextArgs.length > 0 ? { nextAction: `Ask the user, then call save_property_details with: ${nextArgs.join(', ')}.` } : {}),
          remainingQuestions: this.remainingQuestions(accepted),
        });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // 5. save_co_applicant — both co-applicant pages, one tool
  // ---------------------------------------------------------------------------

  private saveCoApplicant(): WebMcpTool {
    return {
      name: 'save_co_applicant',
      description: `Saves whether the user has a co-applicant, and if so
the co-applicant's name and date of birth. Confirm the co-applicant details
with the user before saving.`,
      inputSchema: {
        type: 'object',
        properties: {
          has_co_applicant: { type: 'boolean', description: 'True when another person shares the home\u2019s policy' },
          first_name: { type: 'string', description: 'Co-applicant\u2019s legal first name' },
          last_name: { type: 'string', description: 'Co-applicant\u2019s legal last name' },
          dob: { type: 'string', description: 'Co-applicant\u2019s date of birth, YYYY-MM-DD' },
        },
        required: ['has_co_applicant'],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false, consequentialHint: false, untrustedContentHint: false },
      execute: async (args, options) => {
        const a = args as { has_co_applicant?: boolean; first_name?: string; last_name?: string; dob?: string };
        const isCancelled = () => options?.signal?.aborted === true;
        const notStarted = this.requireQuoteStarted();
        if (notStarted) {
          return notStarted;
        }
        if (typeof a.has_co_applicant !== 'boolean') {
          return errorOutcome('Provide has_co_applicant as true or false.');
        }

        // Validate before any API call so a bad answer never half-saves the branch.
        const firstName = a.first_name?.trim();
        const lastName = a.last_name?.trim();
        if (a.has_co_applicant && (!firstName || !lastName)) {
          return errorOutcome('Missing co-applicant name. Provide first_name and last_name.');
        }
        if (a.has_co_applicant && !isRealDate(a.dob)) {
          return errorOutcome('Invalid co-applicant date of birth. Provide the date in YYYY-MM-DD format.');
        }

        // CIR quirk: /quote/co-applicant encodes include_coapplicant as 'false' | null
        // (false = no, null = yes); the payload mapper encodes it from the store.
        // /quote/co-applicant-info hardcodes include_coapplicant to 'true'.
        const coApplicant: QuoteDraftCoApplicant | undefined = a.has_co_applicant && firstName && lastName && isRealDate(a.dob) ? { firstName, lastName, dob: a.dob } : undefined;
        this.quoteStore.patchStore({
          hasCoApplicant: a.has_co_applicant,
          ...(coApplicant ? { coApplicant } : {}),
        });
        try {
          let response = await firstValueFrom(this.pagesApi.save(this.pagePayload('/quote/co-applicant'), '/quote/co-applicant'));
          if (isCancelled()) {
            return errorOutcome('Save was cancelled.');
          }

          if (a.has_co_applicant) {
            response = await firstValueFrom(this.pagesApi.save(this.pagePayload('/quote/co-applicant-info'), '/quote/co-applicant-info'));
            if (isCancelled()) {
              return errorOutcome('Save was cancelled.');
            }
          }

          this.recordPage(response);
          this.advanceUi(response);
          this.markSubmitted('/quote/co-applicant', ['hasCoApplicant']);
          if (a.has_co_applicant) {
            this.markSubmitted('/quote/co-applicant-info', ['coApplicant']);
          }
          if (response.redirect_to) {
            return okOutcome({ saved: true, redirectedTo: sanitizeRedirect(response.redirect_to), remainingQuestions: this.remainingQuestions() });
          }
          return okOutcome({ saved: true, nextStep: response.page?.path, remainingQuestions: this.remainingQuestions(), ...this.pagePointer(response.page?.path) });
        } catch (err) {
          if ((err as { status?: number })?.status === 422) {
            return errorOutcome('The quote service rejected the co-applicant details. Re-confirm them with the user.');
          }
          return errorOutcome(`Failed to save co-applicant: ${describeError(err)}`);
        }
      },
    };
  }

  // ---------------------------------------------------------------------------
  // 6. get_packages — read-only compare packages
  // ---------------------------------------------------------------------------

  private getPackages(): WebMcpTool {
    return {
      name: 'get_packages',
      description: `Returns Kin's home quote packages — Kin Basics, Kin Plus,
Kin Premium — with premium, deductible, and coverage details for comparison.
If the quote is incomplete, returns the remaining questions to ask before
quotes can be generated.`,
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true, consequentialHint: false, untrustedContentHint: false },
      execute: async () => {
        const notStarted = this.requireQuoteStarted();
        if (notStarted) {
          return notStarted;
        }
        try {
          // Same fetch the compare page performs, mapped to the shape it presents.
          const packages = await firstValueFrom(this.packagesApi.getPackages());
          const windMitApplicable = isCheckWindMitApplicable(packages);
          return okOutcome({
            ready: true,
            windMitigationAvailable: windMitApplicable,
            packages: mapComparePackageData(packages, windMitApplicable).map((pkg) => ({
              title: pkg.legacyTitle,
              presentedTitle: pkg.presentedTitle,
              total: pkg.total,
              totalWithWindMit: pkg.totalWithWindMit,
              totalWithoutWindMit: pkg.totalWithoutWindMit,
              coverageAttributes: pkg.coverageAttributes,
              bullets: pkg.bullets,
            })),
          });
        } catch (err) {
          // Incomplete quote (or service error) — guide the agent to finish the funnel first.
          return okOutcome({
            ready: false,
            remainingQuestions: this.remainingQuestions(),
            note: 'Quotes generate once the property questions are answered. Call save_property_details, then get_packages.',
            ...(this.quoteState() === 'calculating' ? {} : { error: describeError(err) }),
          });
        }
      },
    };
  }

  // ---------------------------------------------------------------------------
  // 7. select_package — consequential package commit
  // ---------------------------------------------------------------------------

  private selectPackage(): WebMcpTool {
    return {
      name: 'select_package',
      description: `Commits the user's chosen quote package and routes the app
to quote review for the checkout handoff. Confirm the choice with the user
before calling. This selects a package for purchase review; payment and
purchase happen afterwards in checkout.`,
      inputSchema: {
        type: 'object',
        properties: {
          selected_package: { type: 'string', enum: [...PACKAGE_TITLES], description: 'Title of the chosen package, from get_packages' },
        },
        required: ['selected_package'],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false, consequentialHint: true, untrustedContentHint: false },
      execute: async (args, options) => {
        const { selected_package: selectedPackage } = args as { selected_package?: string };
        const isCancelled = () => options?.signal?.aborted === true;
        if (!isPackageTitle(selectedPackage)) {
          return errorOutcome('Unknown package. Call get_packages first, then pass one of its package titles.');
        }
        const notStarted = this.requireQuoteStarted();
        if (notStarted) {
          return notStarted;
        }
        try {
          // Mirrors the compare page's submit flow: select the package (its response carries
          // the review redirect), persist the choice on the customer input response, then
          // navigate exactly as a human click would.
          // The compare page applies the wind-mitigation discount whenever the quote is
          // eligible for it (windMitAppliedSignal defaults to isCheckWindMitApplicable).
          const packages = await firstValueFrom(this.packagesApi.getPackages());
          const windMitApplied = isCheckWindMitApplicable(packages);
          const selection = await firstValueFrom(this.packagesApi.selectPackage({ discounts: { wind_mit_applied: windMitApplied }, package: selectedPackage, payment_schedule: 'full' }));
          if (isCancelled()) {
            return errorOutcome('Selection was cancelled.');
          }
          await firstValueFrom(this.customerInputResponseApi.updateCustomerInputResponse({ selected_package: selectedPackage }));
          if (isCancelled()) {
            return errorOutcome('Selection was cancelled.');
          }
          this.quoteStore.patchStore({ selectedPackage });
          await this.router.navigate([selection.redirect_to]);
          return okOutcome({ selected: selectedPackage, reviewPath: selection.redirect_to, note: 'Review and checkout continue in the app UI; payment is not taken here.' });
        } catch (err) {
          return errorOutcome(`Failed to select package: ${describeError(err)}`);
        }
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Shared helpers
  // ---------------------------------------------------------------------------

  /** Derives the agent-facing quote state from the last observed funnel page — a local read, never a poll. */
  private quoteState(): 'not_started' | 'collecting' | 'calculating' | 'ready' | 'ejected' {
    if (!this.quoteStore.cirId()) {
      return 'not_started';
    }
    const path = this.currentFunnelPage() ?? '';
    if (path.startsWith('thanks/')) {
      return 'ejected'; // funnel routed to a Kin ejection page (moratorium, coming-soon, …)
    }
    if (this.quoteStore.storeStatus() === 'loading') {
      return 'calculating'; // the UI is mid-save/polling; the agent should re-check shortly
    }
    if (path.includes('compare') || path.includes('review')) {
      return 'ready'; // packages are available for comparison
    }
    return 'collecting';
  }

  private nextAction(): string {
    switch (this.quoteState()) {
      case 'not_started':
        return 'Call start_quote with the user\u2019s property address.';
      case 'collecting': {
        const pointer = this.pagePointer(this.currentFunnelPage());
        return pointer.nextAction ?? 'Ask the user the remaining questions and call save_property_details (or save_applicant / save_co_applicant).';
      }
      case 'calculating':
        return 'Kin is calculating. Re-check with get_quote_status shortly — typically under 30s.';
      case 'ready':
        return 'Present the packages from get_packages and confirm a choice with the user, then call select_package.';
      case 'ejected':
        return `The funnel routed to "${this.currentFunnelPage()}" — the user continues in the app UI.`;
    }
  }

  /** Guard shared by every state-mutating tool: a quote must be in progress. */
  private requireQuoteStarted(): WebMcpToolOutcome | undefined {
    return this.quoteStore.cirId() ? undefined : errorOutcome('No quote in progress. Call start_quote with the property address first.');
  }

  /**
   * Mirrors what the UI's own save effect does after every save: load the next page's form
   * options and navigate the browser to it. Tool saves must move the funnel in the UI too —
   * otherwise the tool reports "saved" while the user's screen stays on the answered page,
   * and an agent watching that mismatch concludes the save failed and clicks the DOM instead.
   * Cross-origin redirects are left alone: following one would replace the app (and these
   * tools) entirely — the tool's response already relays where the user should go.
   */
  private advanceUi(response: LegacyApiPagesResponse): void {
    if (response.page) {
      const formOptions = this.optionsStore.getLegacyOptionsForForm(response.page.path, response.page.components, this.logger);
      this.optionsStore.updateOptionsForFormByLegacyPath(response.page.path, formOptions, this.logger);
      void this.router.navigate(['quote/legacy', response.page.path]);
    } else if (response.redirect_to && !/^https?:\/\//i.test(response.redirect_to)) {
      const [path, queryString] = response.redirect_to.split('?', 2);
      const queryParams = queryString ? Object.fromEntries(new URLSearchParams(queryString)) : undefined;
      void this.router.navigate([path], queryParams ? { queryParams } : undefined);
    }
  }

  /**
   * Records the page a Pages API response rendered so the state tools and the next walk
   * seed read locally instead of re-polling. Responses reaching here are never layout
   * 'loading' — pagesApi.save/pollForNextPage poll internally until the page is ready.
   */
  private recordPage(response: LegacyApiPagesResponse): void {
    if (response.page) {
      this.lastPagePath = response.page.path;
    } else if (response.redirect_to) {
      // Redirects can be cross-origin (resume flows) and carry PII in their query strings —
      // record only in-app funnel paths, query stripped.
      const redirect = response.redirect_to;
      if (/^https?:\/\//i.test(redirect)) {
        return;
      }
      this.lastPagePath = redirect.split('?')[0]!;
    }
  }

  /**
   * The quote's current funnel page: the last one a Pages API response or the app's own
   * navigation moved it to, falling back to the URL when nothing has been observed yet
   * (e.g. a fresh session where only the user has been driving).
   */
  private currentFunnelPage(): string | undefined {
    return this.lastPagePath ?? funnelPagePathFromUrl(this.router.url);
  }

  /**
   * Pointer for a page the funnel just moved to: the question(s) it asks and the exact tool
   * + arguments that answer them. Agents fall back to clicking the DOM when the tool
   * mapping is not spelled out at the stop — so every stop points at the next tool call.
   */
  private pagePointer(path: string | undefined): { nextQuestions?: string[]; nextAction?: string } {
    if (!path) {
      return {};
    }
    const entry = WALK_BY_PATH[path];
    if (!entry || entry.args.length === 0) {
      return {};
    }
    return {
      nextQuestions: entry.args.map((arg) => arg.prompt),
      nextAction: `Ask the user, then call save_property_details with: ${entry.args.map((arg) => arg.arg).join(', ')}.`,
    };
  }

  /**
   * Marks the given store fields as submitted for a page's form, exactly like the UI save
   * effect does — so `get_quote_status` (`answeredForms`, `remainingQuestions`) and form
   * locking reflect tool-driven saves, not just UI saves.
   */
  private markSubmitted(path: LegacyQuotePagesPath, storeKeys: readonly string[]): void {
    const formKey = mapLegacyPathToFormKey(path);
    if (!formKey) {
      return;
    }
    this.quoteStore.patchStore({ submittedFields: mergeSubmittedFields(this.quoteStore.submittedFields(), formKey, [...storeKeys]) });
  }

  /**
   * Builds the CIR payload for a page the same way the UI save effect does: the store is
   * patched first, then the payload is mapped from the store — so there is exactly one
   * store→CIR mapping in the codebase (`mapLegacyPathToLegacyPayload`).
   */
  private pagePayload(path: LegacyQuotePagesPath): Record<string, unknown> {
    const payload = mapLegacyPathToLegacyPayload(this.quoteStore as unknown as QuoteDraftSelectorState, path);
    if (!payload) {
      throw new Error(`No payload mapping for path "${path}"`);
    }
    return payload as Record<string, unknown>;
  }

  /** Where the property-details walk starts: the observed current page, or the first answered question. */
  private walkSeed(args: SavePropertyDetailsArgs): LegacyQuotePagesPath | undefined {
    // The observed page path is plain string state; the walk table only maps known paths.
    const current = this.currentFunnelPage() as LegacyQuotePagesPath | undefined;
    if (current && WALK_BY_PATH[current]) {
      return current;
    }
    const firstAnswered = FUNNEL_WALK.find((entry) => entry.args.some((arg) => args[arg.arg] !== undefined));
    return firstAnswered?.path;
  }

  private remainingQuestions(answered: readonly string[] = []): string[] {
    return remainingQuestions(answered, this.quoteStore.submittedFields());
  }

  // ---------------------------------------------------------------------------
  // Tracking
  // ---------------------------------------------------------------------------

  /**
   * Wraps the tool's execute so every agent invocation is tracked via FullStory, and serializes
   * the outcome to the agent-facing wire format at this single boundary. The wrapper is
   * transparent to the registration site — it is structurally a ModelContextTool.
   */
  private withTracking(tool: WebMcpTool): ModelContextTool {
    const execute = tool.execute;
    const isWrite = tool.annotations?.readOnlyHint !== true;
    return {
      ...tool,
      execute: async (args, options) => {
        const start = Date.now();
        // Single-writer lock: concurrent write tools race on the shared store (see
        // writeInFlight). Reads run freely.
        if (isWrite && this.writeInFlight) {
          const busy = errorOutcome('Another save is already in progress. Wait for it to finish (check get_quote_status) and retry.');
          this.trackToolExecution(tool.name, busy, start);
          return toWireResult(busy);
        }
        if (isWrite) {
          this.writeInFlight = true;
        }
        try {
          const outcome = await execute(args, options);
          // A cancelled invocation never completed — the browser discards its result —
          // so it is not tracked and does not pollute the success metric.
          if (options?.signal?.aborted !== true) {
            this.trackToolExecution(tool.name, outcome, start);
          }
          return toWireResult(outcome);
        } catch (err) {
          if (options?.signal?.aborted !== true) {
            this.trackToolExecution(tool.name, undefined, start);
          }
          throw err;
        } finally {
          if (isWrite) {
            this.writeInFlight = false;
          }
        }
      },
    };
  }

  /**
   * Sends a FullStory custom event for a completed tool execution. Only the tool name is
   * tracked — never the args, since the tools accept a street address and personal details.
   * A tracking failure is logged and swallowed so analytics can never break the tool.
   */
  private trackToolExecution(toolName: string, outcome: WebMcpToolOutcome | undefined, start: number): void {
    const payload: WebMcpToolExecutedPayload = {
      webmcp_tool_name: toolName,
      webmcp_tool_success: outcome?.ok ?? false,
      webmcp_tool_duration_ms: Date.now() - start,
    };
    try {
      this.fullstory.trackCustomEvent('webmcp_tool_executed', payload);
    } catch (err) {
      console.warn('[WebMCP] Failed to track tool execution', err);
    }
  }
}

// ---------------------------------------------------------------------------
// Local constants — single-tool concerns that don't belong in the funnel-walk table.
// ---------------------------------------------------------------------------

/** Kin's compare packages, `satisfies`-checked against the compare form type. */
const PACKAGE_TITLES = ['Kin Basics', 'Kin Plus', 'Kin Premium'] as const satisfies readonly LegacyPackageTitle[];
type PackageTitle = (typeof PACKAGE_TITLES)[number];

/** Real guard (not a cast): the schema enum and the check both derive from PACKAGE_TITLES. */
const isPackageTitle = (value: unknown): value is PackageTitle => (PACKAGE_TITLES as readonly string[]).includes(value as string);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?1?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Accepts YYYY-MM-DD and rejects impossible dates (e.g. month 13, Feb 30). */
const isRealDate = (value: string | undefined): value is TDateISODate => {
  if (!value || !ISO_DATE_RE.test(value)) {
    return false;
  }
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};
